/**
 * Tracking Partner Registry
 *
 * Provides a type-safe, consent-aware way to register and initialize
 * tracking/ad partners. Forces all partners to go through consent checks.
 *
 * Usage:
 * ```typescript
 * // Register a partner
 * trackingRegistry.register({
 *   id: 'google_ads',
 *   name: 'Google Custom Search Ads',
 *   initialize: (response) => initializeGoogleAds(response),
 *   requiresExplicitConsent: false, // Server handles personalization
 * });
 *
 * // Initialize all partners (consent-checked)
 * trackingRegistry.initializeAll(response);
 * ```
 */

import {
  ConsentState,
  getConsentState,
  isGPCActive,
} from "@ascent/consent/client";
import { CONSOLE_CAPTURE_PREFIX } from "@ascent/debugger";
import type * as PAPICore from "@ascent/papi-core";

/**
 * Consent mode for initialization.
 */
export type ConsentMode = "full" | "limited" | "blocked" | "exempt";

/**
 * Configuration for a tracking partner.
 */
export interface TrackingPartner {
  /** Unique identifier for the partner */
  id: string;

  /** Human-readable name for logging */
  name: string;

  /**
   * Initialize the tracking partner with full consent.
   * Called when user has consented to all tracking.
   */
  initialize: (response: PAPICore.Response) => void | Promise<void>;

  /**
   * Optional: Initialize the tracking partner in limited/non-personalized mode.
   * Called when user has NOT consented but the partner supports a privacy-safe mode.
   *
   * If this is provided, the partner will run in limited mode instead of being blocked.
   * The server should have already adjusted the response (e.g., personalized_ads: false).
   *
   * Example: Google CSA can run with non-personalized ads.
   */
  initializeLimited?: (response: PAPICore.Response) => void | Promise<void>;

  /**
   * Whether this partner is exempt from consent checks entirely.
   *
   * When true:
   * - Partner runs regardless of consent state (including GPC)
   * - Always calls initialize() (never initializeLimited)
   * - Should only be used for essential/security services (e.g., bot detection)
   *
   * @default false
   */
  consentExempt?: boolean;

  /**
   * Whether this partner requires explicit consent for FULL mode (opt-in).
   * - true: Full mode only after explicit user consent (GDPR)
   * - false: Full mode by default, limited/blocked only if user opts out (CCPA)
   *
   * Note: If initializeLimited is provided, the partner runs in limited mode
   * when consent is not given, instead of being fully blocked.
   *
   * Ignored if consentExempt is true.
   * For dynamic determination, use requiresExplicitConsentForResponse instead.
   */
  requiresExplicitConsent: boolean;

  /**
   * Optional: Dynamically determine if explicit consent is required based on response.
   * Takes precedence over the static `requiresExplicitConsent` when provided.
   *
   * @param response - The PAPI response
   * @returns true if explicit consent required (GDPR), false for opt-out (CCPA)
   */
  requiresExplicitConsentForResponse?: (response: PAPICore.Response) => boolean;

  /**
   * Optional: Check if this partner should be enabled based on the response.
   * Return false to skip initialization even with consent.
   */
  isEnabled?: (response: PAPICore.Response) => boolean;

  /**
   * Optional: Check if limited mode is available for this response.
   * Only called if initializeLimited is defined.
   * Return false to block instead of running in limited mode.
   *
   * Example: Check if server set personalized_ads: false
   */
  isLimitedModeAvailable?: (response: PAPICore.Response) => boolean;

  /**
   * Optional: Categories of consent required (for granular consent).
   * If not specified, requires full consent.
   */
  consentCategories?: string[];
}

/**
 * Result of initializing a partner.
 */
export interface PartnerInitResult {
  id: string;
  name: string;
  status:
    | "initialized_full" // Full consent, running normally
    | "initialized_limited" // No consent, but running in limited/non-personalized mode
    | "initialized_exempt" // Consent-exempt, always runs
    | "skipped_no_consent" // No consent and no limited mode available
    | "skipped_disabled" // Partner disabled for this response
    | "error"; // Initialization failed
  consentMode: ConsentMode;
  error?: Error;
}

/**
 * Tracking Partner Registry
 *
 * Central registry for all tracking/ad partners. Enforces consent checks
 * and provides consistent initialization patterns.
 */
class TrackingRegistry {
  private partners: Map<string, TrackingPartner> = new Map();
  private initialized: Set<string> = new Set();
  private lastResults: PartnerInitResult[] = [];

  /**
   * Register a tracking partner.
   * Partners must be registered before they can be initialized.
   *
   * @throws Error if partner with same ID already exists
   */
  register(partner: TrackingPartner): void {
    if (this.partners.has(partner.id)) {
      throw new Error(
        `Tracking partner "${partner.id}" is already registered. ` +
          `Use a unique ID for each partner.`,
      );
    }

    this.partners.set(partner.id, partner);

    if (__DEBUG__) {
      console.log(
        `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Registered: ${partner.name} (${partner.id})`,
      );
    }
  }

  /**
   * Determine the consent mode for a partner.
   *
   * @returns "exempt" if consent-exempt, "full" if full consent given,
   *          "limited" if can run in limited mode, "blocked" otherwise
   */
  getConsentMode(partnerId: string, response?: PAPICore.Response): ConsentMode {
    const partner = this.partners.get(partnerId);
    if (!partner) return "blocked";

    // Consent-exempt partners bypass all consent checks (including GPC)
    if (partner.consentExempt) {
      return "exempt";
    }

    // GPC: run in limited/non-personalized mode if available, otherwise block
    if (isGPCActive()) {
      if (partner.initializeLimited) {
        if (partner.isLimitedModeAvailable && response) {
          return partner.isLimitedModeAvailable(response)
            ? "limited"
            : "blocked";
        }
        return "limited";
      }
      return "blocked";
    }

    const consentState = getConsentState();

    // Full consent given - run in full mode
    if (consentState === ConsentState.CONSENTED) {
      return "full";
    }

    // Explicit rejection - check if limited mode is available
    if (consentState === ConsentState.NOT_CONSENTED) {
      // Check if partner supports limited mode
      if (partner.initializeLimited) {
        // Check if limited mode is available for this response
        if (partner.isLimitedModeAvailable && response) {
          return partner.isLimitedModeAvailable(response)
            ? "limited"
            : "blocked";
        }
        // If no availability check, assume limited mode is available
        return "limited";
      }
      // No limited mode support - blocked
      return "blocked";
    }

    // Unknown consent state - determine if explicit consent is required
    // Dynamic function takes precedence over static boolean
    const requiresExplicit =
      response && partner.requiresExplicitConsentForResponse
        ? partner.requiresExplicitConsentForResponse(response)
        : partner.requiresExplicitConsent;

    if (requiresExplicit) {
      // Opt-in model: need explicit consent for full mode
      // Check if limited mode is available
      if (partner.initializeLimited) {
        if (partner.isLimitedModeAvailable && response) {
          return partner.isLimitedModeAvailable(response)
            ? "limited"
            : "blocked";
        }
        return "limited";
      }
      return "blocked";
    }

    // Opt-out model with unknown consent: allow full mode
    return "full";
  }

  /**
   * Check if a partner can be initialized based on consent.
   * @deprecated Use getConsentMode() for more granular control
   */
  canInitialize(partnerId: string): boolean {
    return this.getConsentMode(partnerId) !== "blocked";
  }

  /**
   * Initialize a specific partner based on consent mode.
   *
   * - Full consent: calls initialize()
   * - Limited consent: calls initializeLimited() if available, else blocked
   * - Blocked: skips initialization
   */
  async initializePartner(
    partnerId: string,
    response: PAPICore.Response,
  ): Promise<PartnerInitResult> {
    const partner = this.partners.get(partnerId);

    if (!partner) {
      return {
        id: partnerId,
        name: "Unknown",
        status: "error",
        consentMode: "blocked",
        error: new Error(`Partner "${partnerId}" not registered`),
      };
    }

    // Check if partner is enabled for this response
    if (partner.isEnabled && !partner.isEnabled(response)) {
      if (__DEBUG__) {
        console.log(
          `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Skipped ${partner.name}: disabled for this response`,
        );
      }
      return {
        id: partnerId,
        name: partner.name,
        status: "skipped_disabled",
        consentMode: "blocked",
      };
    }

    // Determine consent mode
    const consentMode = this.getConsentMode(partnerId, response);

    // Blocked - skip initialization
    if (consentMode === "blocked") {
      if (__DEBUG__) {
        console.log(
          `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Blocked ${partner.name}: no consent and no limited mode`,
        );
      }
      return {
        id: partnerId,
        name: partner.name,
        status: "skipped_no_consent",
        consentMode: "blocked",
      };
    }

    // Already initialized
    if (this.initialized.has(partnerId)) {
      if (__DEBUG__) {
        console.log(
          `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] ${partner.name} already initialized`,
        );
      }
      return {
        id: partnerId,
        name: partner.name,
        status: getInitializedStatus(consentMode),
        consentMode,
      };
    }

    // Initialize based on consent mode
    try {
      if (consentMode === "exempt") {
        // Consent-exempt - always run full initialization
        await partner.initialize(response);

        if (__DEBUG__) {
          console.log(
            `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Initialized (exempt): ${partner.name}`,
          );
        }
      } else if (consentMode === "full") {
        // Full consent - use normal initializer
        await partner.initialize(response);

        if (__DEBUG__) {
          console.log(
            `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Initialized (full): ${partner.name}`,
          );
        }
      } else {
        // Limited mode - use limited initializer
        if (!partner.initializeLimited) {
          // This shouldn't happen if getConsentMode is correct, but safety check
          throw new Error(
            "Limited mode requested but initializeLimited not defined",
          );
        }

        await partner.initializeLimited(response);

        if (__DEBUG__) {
          console.log(
            `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Initialized (limited/non-personalized): ${partner.name}`,
          );
        }
      }

      this.initialized.add(partnerId);

      return {
        id: partnerId,
        name: partner.name,
        status: getInitializedStatus(consentMode),
        consentMode,
      };
    } catch (error) {
      console.error(
        `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Error initializing ${partner.name}:`,
        error,
      );
      return {
        id: partnerId,
        name: partner.name,
        status: "error",
        consentMode,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Initialize all registered partners that consent allows.
   */
  async initializeAll(
    response: PAPICore.Response,
  ): Promise<PartnerInitResult[]> {
    const results: PartnerInitResult[] = [];

    if (__DEBUG__) {
      console.log(
        `${CONSOLE_CAPTURE_PREFIX} [TrackingRegistry] Initializing ${this.partners.size} partners...`,
        {
          consentState: getConsentState(),
          gpcActive: isGPCActive(),
        },
      );
    }

    for (const partnerId of this.partners.keys()) {
      const result = await this.initializePartner(partnerId, response);
      results.push(result);
    }

    // Store results for debug panel access
    this.lastResults = results;

    return results;
  }

  /**
   * Get the results from the last initializeAll() call.
   * Useful for debug panels and diagnostics.
   */
  getLastResults(): PartnerInitResult[] {
    return [...this.lastResults];
  }

  /**
   * Get all registered partner IDs.
   */
  getRegisteredPartners(): string[] {
    return Array.from(this.partners.keys());
  }

  /**
   * Get initialization status for all partners.
   */
  getStatus(): Record<
    string,
    { initialized: boolean; consentMode: ConsentMode }
  > {
    const status: Record<
      string,
      { initialized: boolean; consentMode: ConsentMode }
    > = {};

    for (const [id] of this.partners) {
      status[id] = {
        initialized: this.initialized.has(id),
        consentMode: this.getConsentMode(id),
      };
    }

    return status;
  }

  /**
   * Reset the registry (useful for testing).
   */
  reset(): void {
    this.partners.clear();
    this.initialized.clear();
    this.lastResults = [];
  }

  /**
   * Clear initialization state but keep registrations (for testing consent changes).
   */
  resetInitialization(): void {
    this.initialized.clear();
  }
}

/**
 * Maps consent mode to the appropriate initialized status.
 */
function getInitializedStatus(
  consentMode: ConsentMode,
): PartnerInitResult["status"] {
  switch (consentMode) {
    case "exempt":
      return "initialized_exempt";
    case "full":
      return "initialized_full";
    case "limited":
      return "initialized_limited";
    default:
      return "initialized_full";
  }
}

/**
 * Global tracking registry instance.
 * All tracking partners should register with this.
 */
export const trackingRegistry = new TrackingRegistry();

/**
 * Convenience function to register a partner.
 */
export function registerTrackingPartner(partner: TrackingPartner): void {
  trackingRegistry.register(partner);
}
